import { zodResolver } from '@hookform/resolvers/zod'
import { FileUploadOutlined } from '@mui/icons-material'
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, FormLabel, Stack } from '@mui/material'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'
import FileFieldController from '@/components/inputs/FileFieldController'
import { useAlertContext } from '@/contexts/AlertContext'
import SubmitButton from '@/features/bidding/components/SubmitButton'
import { useBulkImportRampCurvesMutation } from '@/features/resource/hooks/useBulkImportRampCurvesMutation'
import { getBulkImportRampCurvesSchema } from '@/features/resource/schemas'
import type { BulkImportRampCurvesSchemaType } from '@/features/resource/types'
import { errorHandler } from '@/utils/errorHandler'

type BulkImportRampCurvesModalProps = {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const BulkImportRampCurvesModal = ({ open, onClose, onSuccess }: BulkImportRampCurvesModalProps) => {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const { bulkImportRampCurves, isPending } = useBulkImportRampCurvesMutation()
  const [fileInputKey, setFileInputKey] = useState(0)

  const form = useForm<BulkImportRampCurvesSchemaType>({
    resolver: zodResolver(getBulkImportRampCurvesSchema(t)),
  })

  const handleClose = () => {
    form.reset()
    onClose()
  }

  const formatValidationError = (errorData: any): string => {
    if (errorData?.status === 'VALIDATION_FAILED') {
      const curveInfo = errorData.curveName
        ? `"${errorData.curveName}" (curve #${errorData.curveNumber})`
        : `curve #${errorData.curveNumber}`

      return t('resources.ramp_curves.bulk_import.error.validation_failed', {
        curveInfo,
        field: errorData.field,
        message: errorData.message,
      })
    }

    return errorData?.message || t('resources.ramp_curves.bulk_import.error.unknown')
  }

  const onSubmit = async (data: BulkImportRampCurvesSchemaType) => {
    try {
      await bulkImportRampCurves(data.file)

      pushAlert({
        message: t('resources.ramp_curves.bulk_import.success.message'),
        severity: 'success',
        title: t('resources.ramp_curves.bulk_import.success.title'),
      })

      handleClose()
      onSuccess()
    } catch (err: any) {
      // Force file input to remount to prevent ERR_UPLOAD_FILE_CHANGED if user modifies file on disk and retries
      setFileInputKey((prev) => prev + 1)

      if (err?.response?.data?.status === 'VALIDATION_FAILED') {
        const comprehensiveMessage = formatValidationError(err.response.data)
        pushAlert({
          message: comprehensiveMessage,
          severity: 'error',
          title: t('resources.ramp_curves.bulk_import.error.title'),
        })
      } else {
        // Handle other errors
        const error = errorHandler(err, t('resources.ramp_curves.bulk_import.error.unknown'))
        pushAlert({
          message: error.message,
          severity: 'error',
          title: t('resources.ramp_curves.bulk_import.error.title'),
        })
      }
    }
  }

  const dialogProps = {
    title: t('resources.ramp_curves.bulk_import.confirmation.title'),
    description: t('resources.ramp_curves.bulk_import.confirmation.description'),
  }

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>{t('resources.ramp_curves.bulk_import.title')}</DialogTitle>
      <FormProvider {...form}>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
          <DialogContent>
            <Stack spacing={2}>
              <Alert severity="info">{t('resources.ramp_curves.bulk_import.info_message')}</Alert>
              <FormLabel required>{t('resources.ramp_curves.bulk_import.file_label')}</FormLabel>
              <FileFieldController
                key={fileInputKey}
                id="file"
                name="file"
                slotProps={{
                  htmlInput: {
                    accept: '.xlsx,.xls',
                    'data-testid': 'bulk-import-ramp-curves-file-upload',
                  },
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <CustomButton disabled={isPending} variant="outlined" onClick={handleClose}>
              {t('common.button.cancel')}
            </CustomButton>
            <SubmitButton
              buttonProps={{
                startIcon: <FileUploadOutlined />,
                disabled: isPending,
                'data-testid': 'bulk-import-ramp-curves-submit',
              }}
              dialogProps={dialogProps}
              shouldShowConfirmation={true}
              onSubmit={form.handleSubmit(onSubmit)}
            >
              {t('resources.ramp_curves.bulk_import.submit')}
            </SubmitButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}

export default BulkImportRampCurvesModal
